import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  Folder,
  FolderPlus,
  House,
  Pencil,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { UploadDropzone } from '@components/UploadDropzone';
import { TableSkeleton } from '@components/medicus-ui/Loaders';
import {
  DOC_ACCEPT,
  crearCarpeta,
  descargarArchivo,
  eliminarArchivo,
  eliminarCarpeta,
  listContenido,
  renombrarCarpeta,
  subirArchivo,
  type Archivo,
  type Carpeta,
} from '@lib/services/documentacion.service';
import { useAuthStore } from '@lib/store/auth.store';
import { isMedicus, UserRole } from '@lib/types';
import { puedeVer } from '@lib/permissions/catalog';

function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: { message?: string }; message?: string } } };
  return e.response?.data?.error?.message ?? e.response?.data?.message ?? 'Ocurrió un error';
}

function errorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } }).response?.status;
}

function formatTamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** Breadcrumb a partir de la ruta: `OP/Retenciones` → [OP, OP/Retenciones]. */
function migasDeRuta(ruta: string): { nombre: string; ruta: string }[] {
  const partes = ruta.split('/').filter(Boolean);
  return partes.map((nombre, i) => ({ nombre, ruta: partes.slice(0, i + 1).join('/') }));
}

const HINT_UPLOAD = 'PDF, Office, imágenes, CSV/TXT o ZIP · máx 200 MB';

/**
 * El contenido se refresca solo cada 30s (y al volver a la pestaña) porque los
 * archivos NO llegan sólo por acá: el repositorio es una carpeta de red y hay
 * documentos que se generan y se copian por otros medios.
 */
const REFRESCO_MS = 30_000;

export function DocumentacionPage() {
  const queryClient = useQueryClient();
  const { user, empresaActivaId } = useAuthStore();
  const [rutaActual, setRutaActual] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  /** Archivo cuya subida chocó con un nombre existente (para ofrecer reemplazo). */
  const [aReemplazar, setAReemplazar] = useState<File | null>(null);
  const [aEliminar, setAEliminar] = useState<Archivo | null>(null);
  const [eliminarError, setEliminarError] = useState<string | null>(null);
  // Carpetas: crear / renombrar / eliminar.
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [nuevaNombre, setNuevaNombre] = useState('');
  const [carpetaError, setCarpetaError] = useState<string | null>(null);
  const [aRenombrar, setARenombrar] = useState<Carpeta | null>(null);
  const [renombreNombre, setRenombreNombre] = useState('');
  const [carpetaAEliminar, setCarpetaAEliminar] = useState<Carpeta | null>(null);

  const esAdminMedicus =
    user?.rol === UserRole.SUPERADMIN_MEDICUS || user?.rol === UserRole.ADMIN_MEDICUS;
  const puedeGestionar = !!user && puedeVer(user, 'view:documentacion-subir');
  // La organización activa la resuelve el store (`reconciliarEmpresaActiva`):
  // con una sola organización queda activa sola, también para Medicus. Acá no
  // se vuelve a deducir, para que la regla viva en un solo lugar.
  const empresaId = empresaActivaId;
  const puedeGestionarAhora = puedeGestionar && !!empresaId;

  // Al cambiar de organización, volvemos a la raíz: cada una tiene su carpeta.
  useEffect(() => {
    setRutaActual('');
  }, [empresaActivaId]);

  const contenido = useQuery({
    queryKey: ['documentacion', empresaId, rutaActual],
    queryFn: () => listContenido(rutaActual),
    enabled: !!empresaId,
    refetchInterval: REFRESCO_MS,
    refetchOnWindowFocus: true,
  });

  /**
   * Vuelve a leer la carpeta de red. No devuelve la promesa a propósito: el
   * resto del codebase llama a `invalidateQueries` pelado y así los callers no
   * necesitan `void` (Sonar marca `void` sobre una promesa — typescript:S3735).
   */
  const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ['documentacion'] });
  };

  const subirMutation = useMutation({
    mutationFn: (v: { file: File; sobrescribir?: boolean }) =>
      subirArchivo({
        file: v.file,
        ruta: rutaActual,
        descripcion,
        sobrescribir: v.sobrescribir,
      }),
    onSuccess: () => {
      setUploadError(null);
      setAReemplazar(null);
      setDescripcion('');
      refrescar();
    },
    onError: (err: unknown, v) => {
      setUploadError(errorMessage(err));
      // 409 = ya existe un archivo con ese nombre. Se ofrece el reemplazo
      // explícito en vez de pisarlo en silencio.
      setAReemplazar(errorStatus(err) === 409 ? v.file : null);
    },
  });

  const eliminarMutation = useMutation({
    mutationFn: (ruta: string) => eliminarArchivo(ruta),
    onSuccess: () => {
      setAEliminar(null);
      setEliminarError(null);
      refrescar();
    },
    onError: (err: unknown) => setEliminarError(errorMessage(err)),
  });

  const crearCarpetaMutation = useMutation({
    mutationFn: (nombre: string) => crearCarpeta(nombre, rutaActual),
    onSuccess: () => {
      setNuevaOpen(false);
      setNuevaNombre('');
      setCarpetaError(null);
      refrescar();
    },
    onError: (err: unknown) => setCarpetaError(errorMessage(err)),
  });

  const renombrarMutation = useMutation({
    mutationFn: (v: { ruta: string; nombre: string }) =>
      renombrarCarpeta(v.ruta, v.nombre),
    onSuccess: () => {
      setARenombrar(null);
      setCarpetaError(null);
      refrescar();
    },
    onError: (err: unknown) => setCarpetaError(errorMessage(err)),
  });

  const eliminarCarpetaMutation = useMutation({
    mutationFn: (ruta: string) => eliminarCarpeta(ruta),
    onSuccess: () => {
      setCarpetaAEliminar(null);
      setCarpetaError(null);
      refrescar();
    },
    onError: (err: unknown) => setCarpetaError(errorMessage(err)),
  });

  const migas = migasDeRuta(rutaActual);
  const carpetaActual = migas.length > 0 ? migas[migas.length - 1].nombre : null;
  const subcarpetas = contenido.data?.carpetas ?? [];
  const archivos = contenido.data?.archivos ?? [];
  const omitidos = contenido.data?.omitidos ?? [];

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Documentación</h1>
        <p className="text-element-mid">
          Carpeta de red compartida con la organización. Lo que se deja acá por cualquier medio
          aparece en esta pantalla, y lo que subís desde acá queda en la carpeta de red.
        </p>
      </header>

      {!empresaId && (
        <p className="status status-info" style={{ marginTop: '1.5rem' }}>
          Seleccioná una organización (arriba) para ver su repositorio.
        </p>
      )}

      {empresaId && (
        <>
          {/* Breadcrumb + acciones */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="rowBetween" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <nav style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                <button
                  className="btn btn-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  onClick={() => setRutaActual('')}
                >
                  <House size={15} /> Documentación
                </button>
                {migas.map((m) => (
                  <span key={m.ruta} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <ChevronRight size={14} className="text-element-mid" />
                    <button className="btn btn-link" onClick={() => setRutaActual(m.ruta)}>
                      {m.nombre}
                    </button>
                  </span>
                ))}
              </nav>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  title="Volver a leer la carpeta de red"
                  onClick={refrescar}
                >
                  <RefreshCw size={14} /> Actualizar
                </button>
                {puedeGestionar && (
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    disabled={!puedeGestionarAhora}
                    onClick={() => {
                      setCarpetaError(null);
                      setNuevaNombre('');
                      setNuevaOpen(true);
                    }}
                  >
                    <FolderPlus size={16} /> Nueva carpeta
                  </button>
                )}
              </div>
            </div>
          </div>

          {contenido.isError && (
            <p className="status status-error" style={{ marginTop: '1rem' }}>
              {errorMessage(contenido.error)}
            </p>
          )}

          {/* Subcarpetas */}
          {contenido.isLoading && (
            <div className="card" style={{ marginTop: '1rem' }}>
              <TableSkeleton rows={3} />
            </div>
          )}
          {subcarpetas.length > 0 && (
            <div className="card" style={{ marginTop: '1rem' }}>
              <h2 className="heading-5">Carpetas</h2>
              <div className="tableWrap">
                <table className="dataTable">
                  <tbody>
                    {subcarpetas.map((c) => (
                      <tr key={c.ruta}>
                        <td style={{ width: '100%' }}>
                          <button
                            className="btn btn-link"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                            onClick={() => setRutaActual(c.ruta)}
                          >
                            <Folder size={18} /> <strong>{c.nombre}</strong>
                          </button>
                        </td>
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          {puedeGestionar && (
                            <button
                              className="btn btn-link btn-link--outline"
                              title="Renombrar carpeta"
                              onClick={() => {
                                setCarpetaError(null);
                                setRenombreNombre(c.nombre);
                                setARenombrar(c);
                              }}
                            >
                              <Pencil size={14} />
                            </button>
                          )}
                          {esAdminMedicus && (
                            <button
                              className="btn btn-link btn-link--danger"
                              style={{ marginLeft: 6 }}
                              title="Eliminar carpeta (debe estar vacía)"
                              onClick={() => {
                                setCarpetaError(null);
                                setCarpetaAEliminar(c);
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Subir a la carpeta actual */}
          {puedeGestionar && (
            <div className="card" style={{ marginTop: '1rem' }}>
              <h2 className="heading-5">
                Subir documento{carpetaActual && <> a «{carpetaActual}»</>}
              </h2>
              {puedeGestionarAhora ? (
                <>
                  <label className="formField" style={{ marginTop: '0.75rem' }}>
                    <span>Descripción (opcional)</span>
                    <input
                      className="input"
                      type="text"
                      placeholder="Ej.: Acta de acuerdo enero 2026"
                      value={descripcion}
                      maxLength={200}
                      onChange={(e) => setDescripcion(e.target.value)}
                      disabled={subirMutation.isPending}
                    />
                  </label>
                  <UploadDropzone
                    allowed={DOC_ACCEPT}
                    hint={HINT_UPLOAD}
                    disabled={subirMutation.isPending}
                    onFile={(file) => {
                      setUploadError(null);
                      setAReemplazar(null);
                      subirMutation.mutate({ file });
                    }}
                  />
                  {subirMutation.isPending && (
                    <p className="status status-info" style={{ marginTop: '1rem' }}>
                      Subiendo…
                    </p>
                  )}
                  {uploadError && (
                    <p className="status status-error" style={{ marginTop: '1rem' }}>
                      {uploadError}
                      {aReemplazar && (
                        <button
                          className="btn btn-link"
                          style={{ marginLeft: 8 }}
                          onClick={() =>
                            subirMutation.mutate({ file: aReemplazar, sobrescribir: true })
                          }
                        >
                          Reemplazar el archivo existente
                        </button>
                      )}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-element-mid" style={{ marginTop: '0.5rem' }}>
                  Seleccioná una organización (arriba) para subir un documento.
                </p>
              )}
            </div>
          )}

          {/* Modal: nueva carpeta */}
          {nuevaOpen && (
            <div className="modalBackdrop" onClick={() => setNuevaOpen(false)}>
              <div className="modalCard" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
                <header>
                  <h2 className="heading-4">Nueva carpeta</h2>
                  <p className="text-element-mid">
                    {carpetaActual ? `Dentro de «${carpetaActual}»` : 'En la raíz'}
                  </p>
                </header>
                <label className="formField" style={{ marginTop: '0.75rem' }}>
                  <span>Nombre</span>
                  <input
                    className="input"
                    type="text"
                    autoFocus
                    value={nuevaNombre}
                    maxLength={80}
                    onChange={(e) => setNuevaNombre(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && nuevaNombre.trim()) {
                        crearCarpetaMutation.mutate(nuevaNombre.trim());
                      }
                    }}
                  />
                </label>
                {carpetaError && (
                  <p className="status status-error" style={{ marginTop: '0.5rem' }}>
                    {carpetaError}
                  </p>
                )}
                <div className="rowBetween" style={{ marginTop: 20 }}>
                  <button className="btn btn-ghost" onClick={() => setNuevaOpen(false)}>
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={!nuevaNombre.trim() || crearCarpetaMutation.isPending}
                    onClick={() => crearCarpetaMutation.mutate(nuevaNombre.trim())}
                  >
                    {crearCarpetaMutation.isPending ? 'Creando…' : 'Crear'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal: renombrar carpeta */}
          {aRenombrar && (
            <div className="modalBackdrop" onClick={() => setARenombrar(null)}>
              <div className="modalCard" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
                <header>
                  <h2 className="heading-4">Renombrar carpeta</h2>
                  <p className="text-element-mid">
                    Se renombra la carpeta real en la red: quien la tenga abierta va a verla con el
                    nombre nuevo.
                  </p>
                </header>
                <label className="formField" style={{ marginTop: '0.75rem' }}>
                  <span>Nombre</span>
                  <input
                    className="input"
                    type="text"
                    autoFocus
                    value={renombreNombre}
                    maxLength={80}
                    onChange={(e) => setRenombreNombre(e.target.value)}
                  />
                </label>
                {carpetaError && (
                  <p className="status status-error" style={{ marginTop: '0.5rem' }}>
                    {carpetaError}
                  </p>
                )}
                <div className="rowBetween" style={{ marginTop: 20 }}>
                  <button className="btn btn-ghost" onClick={() => setARenombrar(null)}>
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={!renombreNombre.trim() || renombrarMutation.isPending}
                    onClick={() =>
                      renombrarMutation.mutate({
                        ruta: aRenombrar.ruta,
                        nombre: renombreNombre.trim(),
                      })
                    }
                  >
                    {renombrarMutation.isPending ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal: eliminar carpeta */}
          {carpetaAEliminar && (
            <div className="modalBackdrop" onClick={() => setCarpetaAEliminar(null)}>
              <div className="modalCard" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
                <header>
                  <h2 className="heading-4">Eliminar carpeta</h2>
                  <p className="text-element-mid">
                    ¿Seguro que querés eliminar <strong>{carpetaAEliminar.nombre}</strong> de la
                    carpeta de red? Solo se puede si está vacía (sin subcarpetas ni archivos).
                  </p>
                </header>
                {carpetaError && (
                  <p className="status status-error" style={{ marginTop: '0.5rem' }}>
                    {carpetaError}
                  </p>
                )}
                <div className="rowBetween" style={{ marginTop: 20 }}>
                  <button className="btn btn-ghost" onClick={() => setCarpetaAEliminar(null)}>
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={eliminarCarpetaMutation.isPending}
                    onClick={() => eliminarCarpetaMutation.mutate(carpetaAEliminar.ruta)}
                  >
                    {eliminarCarpetaMutation.isPending ? 'Eliminando…' : 'Eliminar'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal: eliminar archivo */}
          {aEliminar && (
            <div className="modalBackdrop" onClick={() => setAEliminar(null)}>
              <div className="modalCard" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
                <header>
                  <h2 className="heading-4">Eliminar documento</h2>
                  <p className="text-element-mid">
                    ¿Seguro que querés eliminar <strong>{aEliminar.nombre}</strong> de la carpeta de
                    red? Esta acción no se puede deshacer.
                  </p>
                </header>
                {eliminarError && (
                  <p className="status status-error" style={{ marginTop: '0.5rem' }}>
                    {eliminarError}
                  </p>
                )}
                <div className="rowBetween" style={{ marginTop: 20 }}>
                  <button
                    className="btn btn-ghost"
                    onClick={() => setAEliminar(null)}
                    disabled={eliminarMutation.isPending}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => eliminarMutation.mutate(aEliminar.ruta)}
                    disabled={eliminarMutation.isPending}
                  >
                    {eliminarMutation.isPending ? 'Eliminando…' : 'Eliminar'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Archivos de la carpeta actual */}
          <div className="card" style={{ marginTop: '1rem' }}>
            <h2 className="heading-5">Documentos</h2>
            {contenido.isLoading && <TableSkeleton rows={6} />}
            {contenido.data && archivos.length === 0 && (
              <p className="text-element-mid">
                {subcarpetas.length > 0
                  ? 'Esta carpeta no tiene archivos (entrá a una subcarpeta o subí uno acá).'
                  : 'Esta carpeta está vacía.'}
              </p>
            )}
            {archivos.length > 0 && (
              <div className="tableWrap">
                <table className="dataTable">
                  <thead>
                    <tr>
                      <th>Archivo</th>
                      <th>Tipo</th>
                      <th>Tamaño</th>
                      <th>Subido por</th>
                      <th>Modificado</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivos.map((a) => (
                      <tr key={a.ruta}>
                        <td>
                          <strong>{a.nombre}</strong>
                          {a.descripcion && (
                            <div className="text-element-mid" style={{ fontSize: 12 }}>
                              {a.descripcion}
                            </div>
                          )}
                        </td>
                        <td>
                          {a.extension ? (
                            <span className="status status-info">{a.extension.toUpperCase()}</span>
                          ) : (
                            <span className="text-element-mid">—</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatTamano(a.tamano)}</td>
                        <td>
                          {/* Sin metadato = el archivo llegó por la red, no por acá. */}
                          {a.subidoPor ? (
                            <>
                              {a.subidoPor.nombre}
                              <div className="text-element-mid" style={{ fontSize: 12 }}>
                                {isMedicus(a.subidoPor.rol) ? 'Medicus' : 'Organización'}
                              </div>
                            </>
                          ) : (
                            <span className="text-element-mid" title="Llegó a la carpeta de red por otro medio">
                              —
                            </span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {new Date(a.modificadoAt).toLocaleString('es-AR')}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button
                            className="btn btn-link btn-link--outline"
                            onClick={() => {
                              descargarArchivo(a).catch((err: unknown) => {
                                window.alert(errorMessage(err));
                              });
                            }}
                          >
                            Descargar
                          </button>
                          {esAdminMedicus && (
                            <button
                              className="btn btn-link btn-link--danger"
                              style={{ marginLeft: 6 }}
                              onClick={() => {
                                setEliminarError(null);
                                setAEliminar(a);
                              }}
                              title="Eliminar documento"
                            >
                              Eliminar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Lo que está en la carpeta pero no se lista se DICE, no se descarta. */}
            {omitidos.length > 0 && (
              <p className="status status-warning" style={{ marginTop: '1rem' }}>
                {omitidos.length} elemento{omitidos.length === 1 ? '' : 's'} de esta carpeta no se
                puede{omitidos.length === 1 ? '' : 'n'} mostrar:{' '}
                {omitidos.map((o) => `${o.nombre} (${o.motivo})`).join(', ')}
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
